import { api } from '../../../api/client';
import type { PlmPointCountDetail, PlmPointCountSlide, PointCountType } from './plmPointCount';

/**
 * PLM (Polarized Light Microscopy) result entry — the asbestos-bulk sibling of
 * the SDMS spreadsheet.  Server DTOs mirror
 * `Application/Analysis/Plm/PlmModels.cs`; endpoints are
 * `Api/Controllers/V1/Analysis/PlmController.cs`.
 *
 * Shape (legacy, unchanged): a QC batch has N sequences — ONE PER SAMPLE LAYER
 * (".A", ".B", …) — each with one stereoscopic observation and that
 * observation's exam rows (one per fiber / component found).
 */

// ── server DTOs ──────────────────────────────────────────────────────────────

export interface PlmBatchHeader {
  batchOid: string;
  analyticalBatchId: string | null;
  testMethodId: string | null;
  test: string | null;
  matrix: string | null;
  method: string | null;
  status: number | null;
  statusText: string | null;
  /** SpreadSheetEntry_AnalyticalBatch.TemplateID — the package popup's Template ID. */
  templateId: number | null;
  roomTemp: string | null;
  humidity: string | null;
  isRolledBack: boolean;
  createdByName: string | null;
  /** The batch's test is configured as an "Asbestos_PLM" unconventional analysis. */
  isPlmTest: boolean;
  /** Still open for result entry (Status < 2) — drives the screen's read-only mode. */
  editable: boolean;
  /** Layers in the batch (list view only; 0 on the single-batch read). */
  layerCount: number;
  /** Layers already entered (list view only) — with `layerCount`, the progress. */
  enteredCount: number;
  // v13 (2026-09-28): the on-demand re-read flow + the list view columns.
  jobIds?: string | null;
  createdDate?: string | null;
  firstAnalystOid?: string | null;
  firstAnalyst?: string | null;
  interdupAnalyst?: string | null;
  sampleCount?: number;
  /** In Analysis · Pending Interdup · Interdup In Progress · Ready to Submit. */
  plmStage?: string | null;
}

export interface PlmLayerRow {
  sequenceOid: string;
  /** Null until this layer is saved for the first time. */
  observationOid: string | null;
  sampleL: string | null;
  sort: number;
  /** 0 Pending Result Entry · 1 Pending Sample Prep · 2 Entered. */
  status: number;
  statusText: string | null;
  layerCount: number;
  qcTypeOid: string | null;
  qcType: string | null;
  sampleLogInOid: string | null;
  sampleNo: string | null;
  clientSampleId: string | null;
  sampleLocation: string | null;
  hasMaterial: boolean;
  examCount: number;
  // v13 (2026-09-28): ownership + the on-demand re-read state.
  analyzedByOid?: string | null;
  analyzedByName?: string | null;
  analyzedDate?: string | null;
  /** "<SysSampleCode>|<letter>" — a layer and its re-reads share it. */
  groupKey?: string | null;
  /** 'Intra' | 'Inter' for a re-read; null for a sample layer. */
  reReadKind?: 'Intra' | 'Inter' | null;
  /** QC-yyMMdd-NNNN for a re-read started from PLM. */
  qcId?: string | null;
  reReadAnalystOid?: string | null;
  /** The re-read's analyst has submitted it (a pre-built re-read: Entered). */
  reReadSubmitted?: boolean;
  /** A submitted Inter read was re-opened — no longer a blind comparison. */
  notBlind?: boolean;
}

export interface PlmObservation {
  sequenceOid: string;
  observationOid: string | null;
  sampleL: string | null;
  sampleNo: string | null;
  clientSampleId: string | null;
  sampleLocation: string | null;
  status: number;

  /** The Material Description exactly as STORED: the text the analyst typed
   *  (free text since 2026-10-07). A layer saved before that holds a ';'-joined
   *  list of Materials Oids here; sent back untouched, such a layer is left as
   *  it was. The name predates the free text — it is the save contract. */
  materialIds: string | null;
  /** What to show for `materialIds`: the text, or an older layer's material names. */
  materialNames: string | null;

  positiveStop: boolean;
  fibrous: boolean;
  nonFibrous: boolean;

  homogeneous: boolean;
  nonHomogeneous: boolean;
  layered: boolean;
  otherHomogenity: boolean;
  homogenityText: string | null;

  colorBeige: boolean; colorBlack: boolean; colorBlue: boolean; colorBrown: boolean;
  colorClear: boolean; colorGray: boolean; colorGreen: boolean; colorOrange: boolean;
  colorPink: boolean; colorRed: boolean; colorSilver: boolean; colorTan: boolean;
  colorPurple: boolean; colorWhite: boolean; colorYellow: boolean; colorCyan: boolean;
  colorMagenta: boolean; colorTeal: boolean; colorGold: boolean; colorOther: boolean;
  variousColors: boolean;
  colorText: string | null;

  cementitious: boolean; granular: boolean; firm: boolean; soft: boolean;
  paperLike: boolean; otherTexture: boolean;
  textureText: string | null;

  treatmentDissolve: boolean; treatmentAsh: boolean; treatmentGrind: boolean;
  treatmentHeat: boolean; treatmentMatrix: boolean; treatmentNonFriable: boolean;
  treatmentSolventTreated: boolean; treatmentTease: boolean; treatmentOther: boolean;

  fibersPresent: boolean;
  /** Legacy enum: 1 Yes · 2 No · 3 N/A (null = unset). */
  friability: number | null;
  fiberObservation: string | null;
  noAsbestosDetected: boolean;

  microscopeId: string | null;
  microscope: string | null;
  temperature: string | null;

  // G5 parity (2026-10-15) — not in the read procedure; written beside the save
  // (IPlmStationStore). The Hot Plate / Thermohydrometer devices, the Daily QC
  // read picked for the layer, the station (from the device row picked) and the
  // contamination check (one every 50 layers per station).
  hotPlate: string | null;
  thermohydrometer: string | null;
  dailyQcOid: string | null;
  dailyQcId: string | null;
  station: string | null;
  contaminationCheck: boolean;
  contaminationCheckedByName: string | null;
  contaminationCheckedDate: string | null;

  /** The RI oil the layer was read with (dbo.DispersionOil) — "name - lot". */
  dispersionOilOid: string | null;
  dispersionOilName: string | null;

  sampleNoteOid: string | null;
  sampleNote: string | null;

  analyzedByOid: string | null;
  analyzedByName: string | null;
  analyzedDate: string | null;
  enteredByOid: string | null;
  enteredByName: string | null;
  enteredDate: string | null;
  /** The sample's Received date — what a save stores as the Entered Date, so a
   *  layer that was never saved can already show it. */
  receivedDate?: string | null;
}

export interface PlmExamRow {
  examOid: string;
  sort: number;
  fiberTypeOid: string | null;
  /** The legacy grid caption is "Component". */
  fiberType: string | null;
  fiberColorOid: string | null;
  fiberColor: string | null;
  name: string | null;
  /** Percentage, free text in legacy (may be "<1", "Trace", …). */
  result: string | null;

  typeLow: boolean; typeMedium: boolean; typeHigh: boolean; typeIsotropic: boolean;
  pleochroismYes: boolean; pleochroismNo: boolean; pleochroismColor: string | null;
  morphologyBundles: boolean; morphologyCurved: boolean; morphologyRibbon: boolean;
  morphologySplayed: boolean; morphologyStraight: boolean; morphologyWavey: boolean;
  morphologyUniform: boolean; morphologyText: string | null;
  elongationPositive: boolean; elongationNegative: boolean; elongationBoth: boolean;
  extinctionOblique: boolean; extinctionParallel: boolean; extinctionUndulose: boolean;
  extinctionIncomplete: boolean; extinctionNone: boolean;
  gamma: string | null; alpha: string | null; riOil: string | null;
  lambdaPara: string | null; lambdaPerb: string | null;
  parallelColor: string | null; perpendicularColor: string | null;
}

export interface PlmOption { id: string; name: string | null }

/** One component-name option, keyed to the FiberTypes value that selects its
 *  master list.  Those strings are load-bearing — see AI_Plm_Lookups_SP. */
export interface PlmComponentName { fiberType: string | null; name: string | null }

export interface PlmLookups {
  fiberTypes: PlmOption[];
  componentNames: PlmComponentName[];
  fiberColors: PlmOption[];
  materials: PlmOption[];
  sampleNotes: PlmOption[];
  analysts: PlmOption[];
  /** The live dispersion (RI) oils, named "name - lot number". */
  dispersionOils: PlmOption[];
  /** AI_AppSettings 'PlmDailyQcGate' = 'Enforce' (2026-10-17). False / absent = the
   *  Daily QC is optional for now: no lock, Daily QC ID not required. */
  dailyQcEnforced?: boolean;
  /** AI_AppSettings 'PlmDupSubmitButtons' = 'On' (2026-10-17): true = the separate dup
   *  steps (Start Inter-dup / Submit Read / Re-open Read). False = the SIMPLE flow (the
   *  default). Absent (an older API) reads as 'On' — see plmWorkflow.dupFlowOf. */
  dupSubmitButtons?: boolean;
}

/** One dbo.FiberTypesValues row — the optical-property autofill source, keyed
 *  by fiber NAME. */
export interface PlmFiberTypeValue {
  name: string | null;
  isotropic: string | null;
  birefringence: string | null;
  pleochroism: string | null;
  morphology: string | null;
  extinction: string | null;
  elongation: string | null;
  riGamma: string | null;
  riAlpha: string | null;
  gammaPara: string | null;
  gammaPerp: string | null;
  ri: string | null;
  parallelColor: string | null;
  perpendicularColor: string | null;
}

// ── write payloads ───────────────────────────────────────────────────────────

/** The writable half of an observation. Property names are the contract with
 *  AI_Plm_SaveLayer_SP's OPENJSON paths — do not rename without the SP. */
export type PlmObservationInput = Omit<
  PlmObservation,
  | 'sequenceOid' | 'observationOid' | 'sampleL' | 'sampleNo' | 'clientSampleId'
  | 'sampleLocation' | 'status' | 'materialNames' | 'sampleNote' | 'dispersionOilName'
  | 'analyzedByName' | 'enteredByOid' | 'enteredByName' | 'enteredDate' | 'receivedDate'
  | 'dailyQcOid' | 'contaminationCheckedByName' | 'contaminationCheckedDate'
>;

/** `examOid` null = a new row.  Any existing exam absent from the array is
 *  soft-deleted server-side (the grid is authoritative). */
export type PlmExamInput = Omit<PlmExamRow, 'examOid' | 'fiberType' | 'fiberColor'> & {
  examOid: string | null;
};

export interface PlmSaveLayerInput {
  observation: PlmObservationInput;
  exams: PlmExamInput[];
  /** One Save, one Analyzed Date for all its layers (owner 2026-10-17, G5 parity): the
   *  stamp the first layer of the same Save was answered, sent back on the others. */
  savedAt?: string | null;
}

// ── calls ────────────────────────────────────────────────────────────────────

// MUST carry the /api/v1 prefix — the axios client's baseURL is the API ORIGIN
// only (http://localhost:5020), it does not prepend a version.  Without it every
// PLM call fell through to the API's SPA fallback (MapFallbackToFile), which
// returns index.html with HTTP 200 — so `res.data?.data` was undefined, `?? []`
// made it an empty list, and NOTHING was logged: the PLM list rendered "no
// batches" and a batch rendered "no sample layers" while the SPs were returning
// rows the whole time.  Matches the sibling api files (qcBatch.api.ts:286,
// prepBatch.api.ts:159).
const BASE = '/api/v1/analysis/plm';

type Envelope<T> = { data?: { data?: T } };

export async function fetchPlmBatches(): Promise<PlmBatchHeader[]> {
  const res: Envelope<PlmBatchHeader[]> = await api.get(`${BASE}/batches`);
  return res.data?.data ?? [];
}

// ── Validate mode (2026-09-29) ──────────────────────────────────────────────
// PLM validates through the SHARED SDMS Result Validation endpoints (rule 8):
// validate / save-edits / rollback live in ../SdmsValidationActions. These two
// reads are the only PLM-side additions.

/** Batch Status "Pending SDMS Validation" — the only state Validate accepts
 *  (AnalysisWriter.ValidateOneAsync gate 1). */
export const PLM_PENDING_VALIDATION = 2;

/** Default Settings → "No Self-Review": when on, nobody may validate a batch
 *  they analysed. The SERVER enforces it; this only lets the button say so up
 *  front. Failing to read it leaves the button enabled (the server still refuses). */
export async function fetchPlmNoSelfReview(): Promise<boolean> {
  try {
    const res: { data?: { data?: { dataReviewSettings?: { noSelfReview?: boolean } } } } =
      await api.get('/api/v1/app-config');
    return res.data?.data?.dataReviewSettings?.noSelfReview === true;
  } catch {
    return false;
  }
}

/** One data-package check point (dbo.DataPackageChecklistQC) — the shape of
 *  GET /analysis/result-validation/{batch}/check-points. */
export interface PlmCheckPoint {
  id: string;
  checkPoint: string | null;
  yes: boolean;
  no: boolean;
  na: boolean;
  qualifier: string | null;
  comment: string | null;
}

export async function fetchPlmCheckPoints(batchOid: string): Promise<PlmCheckPoint[]> {
  const res: Envelope<PlmCheckPoint[]> =
    await api.get(`/api/v1/analysis/result-validation/${encodeURIComponent(batchOid)}/check-points`);
  return res.data?.data ?? [];
}

export async function fetchPlmBatch(batchOid: string): Promise<PlmBatchHeader | null> {
  const res: Envelope<PlmBatchHeader> = await api.get(`${BASE}/batches/${batchOid}`);
  return res.data?.data ?? null;
}

export async function fetchPlmLayers(batchOid: string): Promise<PlmLayerRow[]> {
  const res: Envelope<PlmLayerRow[]> = await api.get(`${BASE}/batches/${batchOid}/layers`);
  return res.data?.data ?? [];
}

/** One layer as GET layers/{id} answers it. `layerDailyQcId` (owner 2026-10-17, G5 parity:
 *  PlmV2Store.LayerDailyQcId) = the layer analyst's Daily QC read on the day of analysis —
 *  a passed one first, else the latest — sent while the layer has no Daily QC ID. */
export interface PlmLayerDetail {
  observation: PlmObservation;
  exams: PlmExamRow[];
  contamination?: PlmContamState;
  layerDailyQcId?: string | null;
}

export async function fetchPlmLayer(
  sequenceOid: string,
): Promise<PlmLayerDetail | null> {
  const res: Envelope<PlmLayerDetail> =
    await api.get(`${BASE}/layers/${sequenceOid}`);
  return res.data?.data ?? null;
}

// ── G5 parity (2026-10-15): devices, Daily QC pick, contamination, dups ──────

/** One PLM Instrument Setup row — the four device drop-downs list them. */
export interface PlmStationRow {
  station: string;
  plmMicroscope: string | null;
  stereoMicroscope: string | null;
  hotPlate: string | null;
  thermohygrometer: string | null;
}

/** A PASSED Daily QC read — what the Daily QC ID picker offers. While the Daily QC is
 *  optional (2026-10-17) also the analyst's own reads of today not submitted yet. */
export interface PlmDailyQcChoice {
  oid: string;
  dailyQcId: string | null;
  qcDate: string;
  analystOid: string;
  analystName: string | null;
  referenceId: string | null;
  /** A read of today not submitted yet — shown "(pending)", never auto-filled. */
  pending?: boolean;
}

/** The contamination badge: tone ok · near (41–50) · due (51+) · checked · na. */
export interface PlmContamState {
  station: string | null;
  position: number | null;
  limit: number;
  tone: 'ok' | 'near' | 'due' | 'checked' | 'na';
  text: string;
  /** The last contamination check at the station before this layer — null when none is on record. */
  lastCheckAt?: string | null;
  lastCheckBy?: string | null;
  lastCheckLayer?: string | null;
}

export async function fetchPlmStationRows(): Promise<PlmStationRow[]> {
  const res: Envelope<PlmStationRow[]> = await api.get(`${BASE}/station-rows`);
  return res.data?.data ?? [];
}

/** The remembered names of the header devices and the RI Oil (dbo.AI_PlmUtility,
 *  G5 parity 2026-10-16). A name typed that is not listed is added on Save. All
 *  empty while the table is not on the database. */
export interface PlmUtilityLists {
  plmScope: string[];
  stereoMicroscope: string[];
  hotPlate: string[];
  thermohydrometer: string[];
  oil: string[];
}

export const EMPTY_PLM_UTILITIES: PlmUtilityLists = {
  plmScope: [], stereoMicroscope: [], hotPlate: [], thermohydrometer: [], oil: [],
};

export async function fetchPlmUtilities(): Promise<PlmUtilityLists> {
  const res: Envelope<Partial<PlmUtilityLists>> = await api.get(`${BASE}/utilities`);
  return { ...EMPTY_PLM_UTILITIES, ...(res.data?.data ?? {}) };
}

export async function fetchPlmDailyQcChoices(): Promise<PlmDailyQcChoice[]> {
  const res: Envelope<PlmDailyQcChoice[]> = await api.get(`${BASE}/daily-qc-choices`);
  return res.data?.data ?? [];
}

export async function fetchPlmContamination(
  sequenceOid: string, station: string | null, check: boolean,
): Promise<PlmContamState | null> {
  const res: Envelope<PlmContamState> = await api.get(`${BASE}/layers/${sequenceOid}/contamination`,
    { params: { station: station ?? undefined, check } });
  return res.data?.data ?? null;
}

/** Removes an Intra-dup / Inter-dup row (QC dups are optional — Bin Yu 2026-10-15). */
export async function removePlmDup(sequenceOid: string): Promise<void> {
  await api.delete(`${BASE}/layers/${sequenceOid}/dup`);
}

/** One analysed layer of the Data Center contamination query. */
export interface PlmContamLayerRow {
  analyzedDate: string;
  station: string;
  checked: boolean;
  checkedDate: string | null;
  analyst: string | null;
  checkedBy: string | null;
  jobId: string | null;
  sampleCode: string | null;
  sampleL: string | null;
  run: number;
  prevGap: number;
}

/** Where a station stands NOW — the "Current status" grid (over ALL layers). */
export interface PlmContamCurrentStatus {
  station: string;
  sinceLastCheck: number;
  lastCheckAt: string | null;
  lastCheckedBy: string | null;
  /** OK · Due soon · Check required next layer. */
  status: string;
}

/** One period (month or quarter) × station row of the Summary. */
export interface PlmContamPeriodSummary {
  period: string;
  periodStart: string;
  station: string;
  layers: number;
  checks: number;
  layersPerCheck: number | null;
  maxRun: number;
  overLimit: number;
  checkedBy: string;
  compliant: boolean;
}

export interface PlmContamReport {
  limit: number;
  stations: string[];
  analysts: string[];
  /** Every station + analyst who read a layer there — drives the filter cascade. */
  stationAnalysts?: { station: string; analyst: string }[];
  current: PlmContamCurrentStatus[];
  summary: PlmContamPeriodSummary[];
  overLimit: PlmContamLayerRow[];
  log: PlmContamLayerRow[];
}

/** Opens the contamination report's Preview (PLM_ContaminationCheck_Summary PDF) in a new tab. */
export async function previewPlmContamination(filter: {
  from?: string; to?: string; station?: string; analyst?: string; groupBy?: 'm' | 'q';
}): Promise<void> {
  const res = await api.get(`${BASE}/contamination/report`, {
    responseType: 'blob',
    params: {
      from: filter.from || undefined, to: filter.to || undefined,
      station: filter.station || undefined, analyst: filter.analyst || undefined,
      groupBy: filter.groupBy || undefined,
    },
  });
  const url = URL.createObjectURL(new Blob([res.data as BlobPart], { type: 'application/pdf' }));
  window.open(url, '_blank');
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

export async function fetchPlmContaminationReport(filter: {
  from?: string; to?: string; station?: string; analyst?: string; groupBy?: 'm' | 'q';
}): Promise<PlmContamReport | null> {
  const res: Envelope<PlmContamReport> = await api.get(`${BASE}/contamination`, {
    params: {
      from: filter.from || undefined, to: filter.to || undefined,
      station: filter.station || undefined, analyst: filter.analyst || undefined,
      groupBy: filter.groupBy || undefined,
    },
  });
  return res.data?.data ?? null;
}

/** Adds a PLM material from the Stereoscopic tab (legacy's ＋ beside the
 *  Material combo). Idempotent by name server-side: an existing material is
 *  returned rather than duplicated, with `created:false`. */
export async function createPlmMaterial(name: string): Promise<PlmOption & { created: boolean }> {
  const res: Envelope<{ id: string; name: string; created: boolean }> =
    await api.post(`${BASE}/materials`, { name });
  const d = res.data?.data;
  // The endpoint always returns the material (it is idempotent by name), so a
  // missing body means a transport failure the caller must see, not a silent
  // "nothing added" that would leave the combo unchanged with no explanation.
  if (!d) throw new Error('The material could not be added.');
  return { id: d.id, name: d.name, created: d.created };
}

export async function fetchPlmLookups(): Promise<PlmLookups> {
  const res: Envelope<PlmLookups> = await api.get(`${BASE}/lookups`);
  return res.data?.data ?? {
    fiberTypes: [], componentNames: [], fiberColors: [],
    materials: [], sampleNotes: [], analysts: [], dispersionOils: [],
  };
}

export async function fetchPlmFiberTypeValues(): Promise<PlmFiberTypeValue[]> {
  const res: Envelope<PlmFiberTypeValue[]> = await api.get(`${BASE}/fiber-type-values`);
  return res.data?.data ?? [];
}

// ── point count (Phase 2) ────────────────────────────────────────────────────
// The shapes and the arithmetic live in plmPointCount.ts (the browser mirror of
// Application/Analysis/Plm/PlmPointCountModels.cs); re-exported here so callers
// have one import for the whole PLM surface.

export type { PlmPointCountSlide, PlmPointCountDetail, PointCountType } from './plmPointCount';

/** One fiber's server-derived totals, echoed back by a successful save. */
export interface PlmPointCountTotalsDto {
  fiberName: string;
  totalAp: number;
  totalNap: number;
  anyTrace: boolean;
  percent: number | null;
  resultText: string;
}

export async function fetchPlmPointCount(sequenceOid: string): Promise<PlmPointCountDetail> {
  const res: Envelope<PlmPointCountDetail> = await api.get(`${BASE}/layers/${sequenceOid}/point-count`);
  return res.data?.data ?? { pointCountType: null, fiberNames: [], slides: [] };
}

export async function savePlmPointCount(
  sequenceOid: string, pointCountType: PointCountType, slides: PlmPointCountSlide[],
): Promise<PlmPointCountTotalsDto[]> {
  const res: Envelope<{ totals: PlmPointCountTotalsDto[] }> =
    await api.put(`${BASE}/layers/${sequenceOid}/point-count`, { pointCountType, slides });
  return res.data?.data?.totals ?? [];
}

export async function clearPlmPointCount(sequenceOid: string): Promise<void> {
  await api.delete(`${BASE}/layers/${sequenceOid}/point-count`);
}

export async function savePlmLayer(
  sequenceOid: string, input: PlmSaveLayerInput,
): Promise<{ observationOid: string | null; savedAt?: string | null }> {
  const res: Envelope<{ observationOid: string | null; savedAt?: string | null }> =
    await api.put(`${BASE}/layers/${sequenceOid}`, input);
  return res.data?.data ?? { observationOid: null };
}

/** The batch-level "Save" audit row (owner 2026-10-17, G5 parity: Save_Execute) — the
 *  layers one Save wrote, ", "-joined; call it with the reason armed (X-Audit-Reason).
 *  The server writes nothing without a reason. */
export async function savePlmSaveAudit(batchOid: string, layers: string): Promise<void> {
  await api.post(`${BASE}/batches/${batchOid}/save-audit`, { layers });
}

export async function addPlmLayer(
  sequenceOid: string,
): Promise<{ sequenceOid: string; sampleL: string | null }> {
  const res: Envelope<{ sequenceOid: string; sampleL: string | null }> =
    await api.post(`${BASE}/layers/${sequenceOid}/add-layer`);
  return res.data?.data ?? { sequenceOid: '', sampleL: null };
}

// ── v13 on-demand re-reads (2026-09-28) ──────────────────────────────────────

/** Starts an Intra dup (the layer's own analyst) or an Inter dup (a second
 *  analyst, blind) of a sample layer. The server enforces every rule. */
export async function addPlmReRead(
  sequenceOid: string, kind: 'Intra' | 'Inter',
): Promise<{ sequenceOid: string; sampleL: string | null; qcId: string | null }> {
  const res: Envelope<{ sequenceOid: string; sampleL: string | null; qcId: string | null }> =
    await api.post(`${BASE}/layers/${sequenceOid}/re-read`, { kind });
  return res.data?.data ?? { sequenceOid: '', sampleL: null, qcId: null };
}

/** The re-read's analyst submits it: the blind is lifted, an Inter read locks. */
export async function submitPlmReRead(sequenceOid: string): Promise<void> {
  await api.post(`${BASE}/layers/${sequenceOid}/re-read/submit`);
}

/** Re-opens a submitted re-read; the reason is required and audited. */
export async function reopenPlmReRead(sequenceOid: string, reason: string): Promise<void> {
  await api.post(`${BASE}/layers/${sequenceOid}/re-read/reopen`, { reason });
}

// ── v13 Daily QC — the daily blind reference-slide read (2026-09-28) ─────────

/** A reference slide the analyst may read. The reference TYPE is shown; its
 *  value and acceptable range stay on the server until the read is submitted. */
export interface PlmDailyQcSlide {
  oid: string;
  referenceId: string | null;
  originalSourceId: string | null;
  source: string | null;
  asbestosType: string | null;
  lastRun: string | null;
  daysAgo: number | null;
  /** Read within the SOP window (7 days) — flagged, never blocked. */
  recent: boolean;
  /** Kept for compatibility — always null since a slide is ONE row (G5 parity 2026-10-16). */
  typeName?: string | null;
  /** How many asbestos types the read is graded against (1 on a single-type slide). */
  types?: number;
  material?: string | null;
  matrix?: string | null;
}

/** One Daily QC record. The ref* fields are the Submit snapshot — null while blind. */
export interface PlmDailyQcRow {
  oid: string;
  dailyQcId: string | null;
  qcDate: string;
  analystOid: string;
  analystName: string | null;
  referenceSampleOid: string;
  referenceId: string | null;
  originalSourceId: string | null;
  createdAt: string;
  submittedAt: string | null;
  result: 'Pass' | 'Fail' | null;
  readTypes: string | null;
  readValue: number | null;
  refType: string | null;
  refValue: number | null;
  refLow: number | null;
  refHigh: number | null;
  reopened: boolean;
  /** G5 list columns (2026-10-17): $.analyzedDate (else created), the slide's matrix /
   *  source, $.microscopeId and $.scopeAligned of the read. Absent on older API builds. */
  analyzedDate?: string | null;
  matrix?: string | null;
  source?: string | null;
  plmScope?: string | null;
  scopeAligned?: boolean;
}

export interface PlmDailyQcCheck { label: string; ok: boolean }

/** One reference asbestos type of the performance table (G5 DailyRow). */
export interface PlmDailyQcTypeRow {
  type: string; value: number; low: number; high: number;
  riALow: number | null; riAHigh: number | null; riGLow: number | null; riGHigh: number | null;
  hasRi: boolean;
  /** "8%" · "—" · "not identified" · "None detected". */
  read: string; alpha: string; gamma: string;
  idOk: boolean; pctOk: boolean; riOk: boolean; ok: boolean;
}

/** The performance of a saved read against its slide (G5 DailyVerdict). */
export interface PlmDailyQcPerformance {
  result: 'Pass' | 'Fail';
  readTypes: string;
  readValue: number;
  checks: PlmDailyQcCheck[];
  rows: PlmDailyQcTypeRow[];
  /** Asbestos types reported that the slide does not hold. */
  extra: { name: string | null; result: string | null }[];
}

export interface PlmDailyQcDetail {
  record: PlmDailyQcRow;
  slideAsbestosType: string | null;
  /** The slide's Source and Matrix — header, read-only. */
  slideSource?: string | null;
  slideMatrix?: string | null;
  /** Scope Aligned tick ($.scopeAligned of the stored observation, as G5). */
  scopeAligned?: boolean;
  /** The read's comment ($.comment of the stored observation, as G5). */
  comment?: string | null;
  /** Saved at least once — its performance can be shown. */
  saved?: boolean;
  observation: PlmObservationInput | null;
  exams: PlmExamInput[];
  checks: PlmDailyQcCheck[];
  /** The performance of the read as last saved; null until it is saved. */
  performance?: PlmDailyQcPerformance | null;
  /** A live PLM layer carries this read as its Daily QC (2026-10-17). */
  used?: boolean;
  /** Delete may be offered: pending, unused, and the caller's own read (or an admin). */
  canDelete?: boolean;
}

/** PUT body of a Daily QC save: the layer payload plus Scope Aligned. */
export interface PlmDailyQcSaveInput extends PlmSaveLayerInput {
  scopeAligned: boolean;
  /** The slide picked in Reference Slide ID (owner 2026-10-19, G5 parity): when it
   *  differs from the read's own, the server moves the read to it before saving. */
  referenceSampleOid?: string | null;
}

/** off (no readable slide in the library — the gate is not enforced) · none ·
 *  pending · pass · fail · override. */
export type PlmDailyQcState = 'off' | 'none' | 'pending' | 'pass' | 'fail' | 'override';

export interface PlmDailyQcStatus {
  state: PlmDailyQcState;
  gateActive: boolean;
  /** May the analyst enter PLM results today? */
  allows: boolean;
  today: {
    oid: string; dailyQcId: string | null; submittedAt: string | null; result: string | null;
    readTypes: string | null; readValue: number | null;
    refType: string | null; refValue: number | null; refLow: number | null; refHigh: number | null;
  } | null;
  override: { reason: string | null; createdAt: string; overriddenByName: string | null } | null;
  /** The signed-in user may perform a supervisor override. */
  canOverride: boolean;
  /** The day the gate is judged on (the database's date). */
  date: string | null;
  /** The DQC ID of the analyst's latest read of today while it is not submitted —
   *  New is disabled until it is submitted or deleted (owner 2026-10-19, G5 parity). */
  pendingDailyQcId?: string | null;
}

export async function fetchPlmDailyQcList(mine = false): Promise<PlmDailyQcRow[]> {
  const res: Envelope<PlmDailyQcRow[]> = await api.get(`${BASE}/daily-qc${mine ? '?mine=true' : ''}`);
  return res.data?.data ?? [];
}

export async function fetchPlmDailyQcStatus(): Promise<PlmDailyQcStatus | null> {
  const res: Envelope<PlmDailyQcStatus> = await api.get(`${BASE}/daily-qc/status`);
  return res.data?.data ?? null;
}

export async function fetchPlmDailyQcSlides(): Promise<PlmDailyQcSlide[]> {
  const res: Envelope<PlmDailyQcSlide[]> = await api.get(`${BASE}/daily-qc/slides`);
  return res.data?.data ?? [];
}

export async function fetchPlmDailyQc(oid: string): Promise<PlmDailyQcDetail | null> {
  const res: Envelope<PlmDailyQcDetail> = await api.get(`${BASE}/daily-qc/${oid}`);
  return res.data?.data ?? null;
}

/** Starts a Daily QC on a slide; returns the new record's Oid + DQC id. */
export async function createPlmDailyQc(
  referenceSampleOid: string,
  asbestosType?: string | null,
): Promise<{ oid: string; dailyQcId: string | null }> {
  const res: Envelope<{ oid: string; dailyQcId: string | null }> =
    await api.post(`${BASE}/daily-qc`, { referenceSampleOid, asbestosType: asbestosType ?? null });
  return res.data?.data ?? { oid: '', dailyQcId: null };
}

/** Saves the read; refused (400, G5 wording) while a required field is missing.
 *  Returns the performance of the read as saved. */
export async function savePlmDailyQc(oid: string, input: PlmDailyQcSaveInput): Promise<PlmDailyQcPerformance | null> {
  const res: Envelope<{ saved: boolean; performance: PlmDailyQcPerformance | null }> =
    await api.put(`${BASE}/daily-qc/${oid}`, input);
  return res.data?.data?.performance ?? null;
}

/** Submits the read — graded on the server against the slide's reference type(s).
 *  `blocked`: a failed read with nothing else (an earlier pass, an override) allowing today. */
export async function submitPlmDailyQc(oid: string): Promise<{ result: 'Pass' | 'Fail' | null; blocked: boolean }> {
  const res: Envelope<{ result: 'Pass' | 'Fail'; blocked: boolean }> = await api.post(`${BASE}/daily-qc/${oid}/submit`);
  return { result: res.data?.data?.result ?? null, blocked: !!res.data?.data?.blocked };
}

/** Sets the read's comment (kept with the read, as G5). */
export async function commentPlmDailyQc(oid: string, comment: string): Promise<void> {
  await api.post(`${BASE}/daily-qc/${oid}/comment`, { comment });
}

export async function rollbackPlmDailyQc(oid: string, reason: string): Promise<void> {
  await api.post(`${BASE}/daily-qc/${oid}/rollback`, { reason });
}

/** Deletes a PENDING Daily QC no PLM layer uses (soft delete, 2026-10-17). The
 *  reason rides X-Audit-Reason — call it inside useDeleteFlow. */
export async function deletePlmDailyQc(oid: string): Promise<void> {
  await api.delete(`${BASE}/daily-qc/${oid}`);
}

/** Supervisor override of an analyst's FAILED read today (reason audited). */
export async function overridePlmDailyQc(analystOid: string, reason: string): Promise<void> {
  await api.post(`${BASE}/daily-qc/override`, { analystOid, reason });
}

/** Copies one layer onto others, replacing each target entirely (Phase 3).
 *  Backs Copy Previous, Copy To All and the Carry Over Results popup's Copy —
 *  they differ only in how the caller picks the source and targets. */
export async function copyPlmLayer(
  sourceSequenceOid: string, targetSequenceOids: string[],
): Promise<{ copied: number; from: string | null }> {
  const res: Envelope<{ copied: number; from: string | null }> =
    await api.post(`${BASE}/layers/${sourceSequenceOid}/copy`, { targetSequenceOids });
  return res.data?.data ?? { copied: 0, from: null };
}

// ── Phase 6: submit → Data Package ──────────────────────────────────────────

/** One reason the batch cannot be submitted yet. `message` is legacy's wording. */
export interface PlmSubmitBlocker {
  code: 'not-entered' | 'analyst-missing' | 'exam-incomplete' | string;
  message: string;
  /** The layers at fault, so the analyst knows where to go. */
  detail: string | null;
}

export async function fetchPlmSubmitCheck(
  batchOid: string,
): Promise<{ canSubmit: boolean; blockers: PlmSubmitBlocker[] }> {
  const res: Envelope<{ canSubmit: boolean; blockers: PlmSubmitBlocker[] }> =
    await api.get(`${BASE}/batches/${batchOid}/submit-check`);
  return res.data?.data ?? { canSubmit: false, blockers: [] };
}

/** What the Data Package find-or-create hands back: enough to open the shared
 *  package popup with its persisted header and the right terminal action. */
export interface PlmDataPackage {
  oid: string | null;
  /** DataPackage.Status — 1 PendingSubmission, so the popup offers Submit. */
  status: number | null;
  /** Failing QC rows with no qualifier; legacy asks to confirm before opening. */
  qcFailCount: number;
  packageCreatedDate: string | null;
  batchCreatedBy: string | null;
}

/** Creates (or finds) the batch's Data Package, which the caller then OPENS.
 *
 *  Deliberately the EXISTING SDMS endpoint, not a PLM-specific one: creating a
 *  Data Package is the same operation for a PLM batch as for an SDMS one, and
 *  it already seeds the check points and applies its own gates
 *  (CLAUDE.md rule 8).
 *
 *  This does NOT submit the batch, and must not be reported as if it had.
 *  Legacy's Complete (QCBatchController.AddDataPackage_Execute :13862)
 *  find-or-creates the package and opens DataPackage_DetailView as a modal; the
 *  batch advances Pending Submission → Pending Validation only when the analyst
 *  answers the check points and presses Submit INSIDE that popup (QBIDSubmit).
 *  Creating the package alone leaves the QC Batch row on Pending Submission. */
export async function createPlmDataPackage(batchOid: string): Promise<PlmDataPackage> {
  const res: Envelope<PlmDataPackage> =
    await api.post(`/api/v1/analysis/sdms/${batchOid}/data-package`);
  return res.data?.data ?? {
    oid: null, status: null, qcFailCount: 0, packageCreatedDate: null, batchCreatedBy: null,
  };
}

// ── Phase 5: report preview ─────────────────────────────────────────────────

/** The report names the toolbar dropdown offers — the lab's configured PLM
 *  final reports, or the built-in layout when it has none. */
export async function fetchPlmReportNames(): Promise<string[]> {
  const res: Envelope<string[]> = await api.get(`${BASE}/report-names`);
  return res.data?.data ?? [];
}

/** Renders the PLM Results report and opens it in a new tab, mirroring the
 *  legacy `window.open` of the exported PDF. Returns false when the browser
 *  blocked the popup, so the caller can say so instead of failing silently. */
/** Downloads the monthly PLM QC report PDF (prototype v18 QC Charts →
 *  "Export monthly report"). `month` is "yyyy-MM". */
export async function downloadPlmQcMonthlyReport(month: string): Promise<void> {
  const res = await api.get(`${BASE}/qc-report?month=${encodeURIComponent(month)}`, { responseType: 'blob' });
  const blob = new Blob([res.data as BlobPart], { type: 'application/pdf' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `PLM-QC-${month}.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

export async function previewPlmReport(batchOid: string, name: string): Promise<boolean> {
  const res = await api.get(
    `${BASE}/batches/${batchOid}/report?name=${encodeURIComponent(name)}`,
    { responseType: 'blob' },
  );
  const blob = new Blob([res.data as BlobPart], { type: 'application/pdf' });
  const url = URL.createObjectURL(blob);
  const win = window.open(url, '_blank');
  // Revoke once the tab has had a chance to load it; revoking immediately can
  // race the open in some browsers.
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
  return win !== null;
}

// ── Phase 4: reset · comment · delete ───────────────────────────────────────

/** Blanks every layer in the batch and puts them back to Pending Result Entry.
 *  The layers themselves stay — Reset is "start this batch again". */
export async function resetPlmBatch(batchOid: string): Promise<{ layersReset: number }> {
  const res: Envelope<{ layersReset: number }> =
    await api.post(`${BASE}/batches/${batchOid}/reset`);
  return res.data?.data ?? { layersReset: 0 };
}

/** Files a comment against the batch's job(s) — one dbo.Notes row per job. */
export async function addPlmComment(
  batchOid: string, title: string, text: string,
): Promise<{ jobCount: number }> {
  const res: Envelope<{ jobCount: number }> =
    await api.post(`${BASE}/batches/${batchOid}/comment`, { title, text });
  return res.data?.data ?? { jobCount: 0 };
}

/** Deletes the whole QC batch. Deliberately the EXISTING QC-batch endpoint, not
 *  a PLM-specific one: deleting a PLM batch IS deleting a QC batch, and that
 *  procedure was extended with the PLM cascade rather than duplicated
 *  (CLAUDE.md rule 8). It keeps its own guards — already-reported samples,
 *  entered results, and a batch past the deletable stage. */
export async function deletePlmBatch(batchOid: string): Promise<void> {
  await api.delete(`/api/v1/analysis/qc-batches/${batchOid}`);
}

export async function removePlmLayer(sequenceOid: string): Promise<void> {
  await api.delete(`${BASE}/layers/${sequenceOid}`);
}

/** One row of the "PLM Results" grid inside the Data Package popup — legacy's
 *  PLMStereoscopicObservation_ListView_PLMDPPreview, same 12 columns. */
export interface PlmDataPackageResultRow {
  sequenceOid: string;
  jobId: string | null;
  sampleId: string | null;
  sampleLayer: string | null;
  sampleName: string | null;
  /** Legacy's !NoAsbestosDetected. */
  asbestosFound: boolean;
  /** Free text — the Asbestos Stereomicroscopic % may be "<1" or "ND". */
  fiberObservation: string | null;
  status: number;
  /** The sequence status text — or "Positive Stop" for a layer reported as
   *  Positive Stop (server rule, G5 parity 2026-10-17). */
  statusText: string | null;
  /** The Material Description as typed (a legacy ';'-joined Oid list reads as the material names). */
  material: string | null;
  analyzedBy: string | null;
  analyzedDate: string | null;
  submittedBy: string | null;
  submittedDate: string | null;
  /** May the CURRENT user tick this row for review? Server-computed: false when
   *  the row is already validated, or "No Self-Review" is on and they analysed it. */
  canReview: boolean;
}

/**
 * One row of AI_Plm_QcCompare_SP — a layer plus ONE of its components. A layer
 * with no components still yields one row with null component fields, so a
 * re-read nobody has entered is representable rather than missing.
 */
export interface PlmQcCompareRow {
  sequenceOid: string;
  /** The SampleLogIn a re-read shares with the layer it re-reads. */
  sampleLogInOid: string | null;
  sampleLayer: string | null;
  /** Text after the last '.' — "A", "B". */
  layerLetter: string | null;
  /** "Sample" · "Intra Re-read" · "Inter Re-read". */
  qcType: string | null;
  status: number;
  analyzedByOid: string | null;
  analyzedByName: string | null;
  noAsbestosDetected: boolean;
  examOid: string | null;
  /** The exam grid's "Component" (dbo.FiberTypes.FiberType). */
  component: string | null;
  name: string | null;
  /** Free text — "<1", "Trace", "12.54". */
  result: string | null;
  /** The SIMPLE dup flow (2026-10-17): this layer is blind to the signed-in user —
   *  the row carries its identity only (no analyst, no components). */
  blind?: boolean;
}

/** One row of AI_Plm_QcHistory_SP — the lab-wide QC feed for the charts. It is
 *  a PlmQcCompareRow plus what a chart needs and a verdict does not: when it
 *  was analysed, and which batch it came from. */
export interface PlmQcHistoryRow extends PlmQcCompareRow {
  batchOid: string | null;
  batchId: string | null;
  analyzedDate: string | null;
  // v13 QC Results list (2026-09-28): the re-read's own record, when it has one.
  qcId?: string | null;
  reReadAnalystOid?: string | null;
  reReadSubmitted?: boolean;
  notBlind?: boolean;
}

/** Lab-wide PLM QC history. Both bounds optional (omit for everything). */
export async function fetchPlmQcHistory(
  from?: string, to?: string, view?: 'records',
): Promise<PlmQcHistoryRow[]> {
  const q = new URLSearchParams();
  if (from) q.set('from', from);
  if (to) q.set('to', to);
  // 'records' = the QC Results list as the signed-in user may see it (owner 2026-10-17,
  // G5 parity — decided on the server: other analysts' open Inter reads left out, blind marked).
  if (view) q.set('view', view);
  const res: Envelope<PlmQcHistoryRow[]> =
    await api.get(`${BASE}/qc-history${q.toString() ? `?${q}` : ''}`);
  return res.data?.data ?? [];
}

/** Every layer of the batch with its components, for the QC Result View tab. */
/** The corrective action recorded on a failed QC pair (prototype v18). */
export interface PlmCorrectiveAction {
  /** The dup's own layer. */
  sequenceOid: string;
  text: string | null;
  byName: string | null;
  at: string | null;
}

export async function fetchPlmCorrectiveActions(batchOid: string): Promise<PlmCorrectiveAction[]> {
  const res: Envelope<PlmCorrectiveAction[]> = await api.get(`${BASE}/batches/${batchOid}/corrective-actions`);
  return res.data?.data ?? [];
}

/** Records (an empty text clears) the corrective action of a dup's failed QC pair. */
export async function savePlmCorrectiveAction(sequenceOid: string, text: string): Promise<void> {
  await api.put(`${BASE}/layers/${sequenceOid}/corrective-action`, { text });
}

export async function fetchPlmQcCompare(batchOid: string): Promise<PlmQcCompareRow[]> {
  const res: Envelope<PlmQcCompareRow[]> =
    await api.get(`${BASE}/batches/${batchOid}/qc-compare`);
  return res.data?.data ?? [];
}

/** The PLM Results grid for the Data Package popup. Read-only, as legacy's view
 *  is (AllowNew / AllowEdit / AllowDelete all False). */
/** @param packageScope true for the SUBMIT popup (G5 Self Submit): with Self Submit On the
 *  server lists only the user's own layers, and a row is selectable while nobody has
 *  submitted it. False = the review read (No-Self-Review rule). */
export async function fetchPlmDataPackageResults(
  batchOid: string,
  packageScope = false,
): Promise<PlmDataPackageResultRow[]> {
  const res: Envelope<PlmDataPackageResultRow[]> =
    await api.get(`${BASE}/batches/${batchOid}/data-package-results`,
      packageScope ? { params: { scope: 'package' } } : undefined);
  return res.data?.data ?? [];
}

